import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, Course, Student, Teacher } from "../models";
import { PROFILE_APPROVAL_STATUS } from "../models/constants/model-constants";
import { formatClassForMobile } from "../utils/class-response";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class TeacherController {
  private normalizeTimeToHHmm(value: unknown): string | null {
    if (value == null) return null;
    let raw: string;
    if (typeof value === "string") {
      raw = value.trim();
    } else if (typeof value === "number" || typeof value === "boolean") {
      raw = String(value).trim();
    } else {
      return null;
    }
    const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (hhmm.test(raw)) return raw;

    // Backward-compat for already-stored ISO-like strings; extract time only.
    const isoMatch = /T(\d{2}:\d{2})/.exec(raw);
    if (isoMatch?.[1] && hhmm.test(isoMatch[1])) return isoMatch[1];
    return null;
  }

  /**
   * Get all teachers for the authenticated school with filtering and pagination
   * @route GET /api/v1/school/teachers
   * @access Private (School Admin)
   * @query status, isApproved, approvalStatus, subject, page, limit, sortBy, sortOrder, search, hasDocuments, isEmailVerified
   */
  async getAllSchoolTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      console.log("schoolId", schoolId);
      const {
        status,
        isApproved,
        approvalStatus,
        subject,
        page = 1,
        limit = 10,
        sortBy = "createdAt",
        sortOrder = "desc",
        search,
        hasDocuments,
        isEmailVerified,
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Build query
      const schoolObjectId = new mongoose.Types.ObjectId(schoolId);
      console.log("schoolObjectId", schoolObjectId);
      const query: any = { schoolId: schoolObjectId };
      console.log("query", query);

      // Debug: Let's check if there are any teachers with this schoolId
      const debugTeachers = await Teacher.find({
        schoolId: schoolObjectId,
      }).lean();
      console.log("Debug - teachers with this schoolId:", debugTeachers.length);
      if (debugTeachers.length > 0) {
        console.log("Debug - first teacher:", debugTeachers[0]);
      }

      // Debug: Let's also check with string comparison
      const debugTeachersString = await Teacher.find({
        schoolId: schoolId,
      }).lean();
      console.log(
        "Debug - teachers with string schoolId:",
        debugTeachersString.length,
      );

      // Add optional filters
      if (status) query.status = status;
      if (isApproved !== undefined) query.isApproved = isApproved === "true";
      if (approvalStatus) query.approvalStatus = approvalStatus;
      if (subject) query["subjects.name"] = { $regex: subject, $options: "i" };
      if (hasDocuments !== undefined) {
        if (hasDocuments === "true") {
          query.documents = { $exists: true, $not: { $size: 0 } };
        } else {
          query.$or = [
            { documents: { $exists: false } },
            { documents: { $size: 0 } },
          ];
        }
      }
      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { employeeId: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
          { phone: { $regex: search, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      ); // Max 100 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [teachers, totalCount, statusCounts] = await Promise.all([
        Teacher.find({ ...query })
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .populate("schoolId", "schoolName")
          .populate("userId", "email isEmailVerified")
          .lean(), // Use lean() for better performance
        Teacher.countDocuments(query),
        // Get counts for all approval statuses for this school
        Teacher.aggregate([
          { $match: { schoolId: schoolObjectId } },
          {
            $group: {
              _id: "$approvalStatus",
              count: { $sum: 1 },
            },
          },
        ]),
      ]);

      // Format status counts
      const statusCountMap: Record<string, number> = {};
      for (const item of statusCounts) {
        statusCountMap[item._id] = item.count;
      }

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      res.status(200).json({
        success: true,
        message: `${totalCount} teachers found for this school`,
        data: {
          teachers: teachers.map(teacher => ({
            id: teacher._id,
            employeeId: teacher.employeeId,
            fullName: teacher.fullName,
            email: (teacher.userId as any)?.email,
            phone: teacher.phone,
            gender: teacher.gender,
            dateOfBirth: teacher.dateOfBirth,
            profilePicture: teacher.profilePicture,
            status: teacher.status,
            approvalStatus: teacher.approvalStatus,
            isApproved: teacher.isApproved,
            subjects: teacher.subjects,
            hireDate: teacher.hireDate,
            createdAt: teacher.createdAt,
            isEmailVerified: (teacher.userId as any)?.isEmailVerified,
            hasDocuments: teacher.documents && teacher.documents.length > 0,
          })),
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            status: status || null,
            isApproved: isApproved || null,
            approvalStatus: approvalStatus || null,
            subject: subject || null,
            search: search || null,
            hasDocuments: hasDocuments || null,
            isEmailVerified: isEmailVerified || null,
          },
          statistics: {
            totalTeachers: totalCount,
            approvalStatusBreakdown: {
              pending: statusCountMap.pending || 0,
              approved: statusCountMap.approved || 0,
              rejected: statusCountMap.rejected || 0,
              cancelled: statusCountMap.cancelled || 0,
            },
            statusBreakdown: {
              active: teachers.filter(t => t.status === "active").length,
              inactive: teachers.filter(t => t.status === "inactive").length,
              on_leave: teachers.filter(t => t.status === "on_leave").length,
              terminated: teachers.filter(t => t.status === "terminated")
                .length,
            },
          },
          sort: {
            field: sortBy as string,
            order: sortOrder as string,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teachers:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get comprehensive statistics for teachers in the authenticated school
   * @route GET /api/v1/school/teachers/statistics
   * @access Private (School Admin)
   */
  async getSchoolTeacherStatistics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Get comprehensive statistics
      const [
        totalTeachers,
        approvalStatusStats,
        statusStats,
        subjectStats,
        recentTeachers,
        pendingCount,
        approvedCount,
        rejectedCount,
        cancelledCount,
      ] = await Promise.all([
        // Total count
        Teacher.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
        }),

        // Approval status breakdown
        Teacher.aggregate([
          { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
          {
            $group: {
              _id: "$approvalStatus",
              count: { $sum: 1 },
            },
          },
        ]),

        // Operational status breakdown
        Teacher.aggregate([
          { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
          {
            $group: {
              _id: "$status",
              count: { $sum: 1 },
            },
          },
        ]),

        // Subject breakdown
        Teacher.aggregate([
          { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
          { $unwind: "$subjects" },
          {
            $group: {
              _id: "$subjects.name",
              count: { $sum: 1 },
            },
          },
          { $sort: { count: -1 } },
          { $limit: 10 },
        ]),

        // Recent teachers (last 7 days)
        Teacher.find({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          createdAt: { $gte: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
        })
          .sort({ createdAt: -1 })
          .limit(5)
          .select("fullName employeeId approvalStatus status createdAt")
          .lean(),

        // Quick counts for common filters
        Teacher.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        }),
        Teacher.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          approvalStatus: PROFILE_APPROVAL_STATUS.APPROVED,
        }),
        Teacher.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          approvalStatus: PROFILE_APPROVAL_STATUS.REJECTED,
        }),
        Teacher.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          approvalStatus: PROFILE_APPROVAL_STATUS.CANCELLED,
        }),
      ]);

      // Format approval status stats
      const approvalStatusMap: Record<string, number> = {};
      for (const item of approvalStatusStats) {
        approvalStatusMap[item._id] = item.count;
      }

      // Format operational status stats
      const statusMap: Record<string, number> = {};
      for (const item of statusStats) {
        statusMap[item._id] = item.count;
      }

      res.status(200).json({
        success: true,
        message: "School teacher statistics retrieved successfully",
        data: {
          overview: {
            totalTeachers,
            pendingApproval: pendingCount,
            approved: approvedCount,
            rejected: rejectedCount,
            cancelled: cancelledCount,
          },
          approvalStatusBreakdown: {
            pending: approvalStatusMap.pending || 0,
            approved: approvalStatusMap.approved || 0,
            rejected: approvalStatusMap.rejected || 0,
            cancelled: approvalStatusMap.cancelled || 0,
          },
          statusBreakdown: {
            active: statusMap.active || 0,
            inactive: statusMap.inactive || 0,
            on_leave: statusMap.on_leave || 0,
            terminated: statusMap.terminated || 0,
          },
          subjectBreakdown: subjectStats,
          recentActivity: {
            newTeachersLast7Days: recentTeachers.length,
            recentTeachers: recentTeachers.map(teacher => ({
              id: teacher._id,
              fullName: teacher.fullName,
              employeeId: teacher.employeeId,
              approvalStatus: teacher.approvalStatus,
              status: teacher.status,
              createdAt: teacher.createdAt,
            })),
          },
          quickStats: {
            pendingApprovalPercentage:
              totalTeachers > 0
                ? Math.round((pendingCount / totalTeachers) * 100)
                : 0,
            approvedPercentage:
              totalTeachers > 0
                ? Math.round((approvedCount / totalTeachers) * 100)
                : 0,
            activePercentage:
              totalTeachers > 0
                ? Math.round(((statusMap.active || 0) / totalTeachers) * 100)
                : 0,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school teacher statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher by ID with full details
   * @route GET /api/v1/school/teachers/:id
   * @access Private (School Admin)
   */
  async getTeacherById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("schoolId", "schoolName establishmentYear")
        .populate("userId", "email isEmailVerified status")
        .populate("approvedBy", "fullName email");

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Get teacher's classes
      const classes = await Class.find({
        teacherId: teacher._id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("semester", "semesterName academicYear")
        .select(
          "className classCode subject status currentStudents maxStudents",
        );

      res.status(200).json({
        success: true,
        message: "Teacher found",
        data: {
          teacher: {
            id: teacher.id,
            userId: teacher.userId,
            schoolId: teacher.schoolId,
            employeeId: teacher.employeeId,
            fullName: teacher.fullName,
            dateOfBirth: teacher.dateOfBirth,
            age: (teacher as any).age,
            gender: teacher.gender,
            phone: teacher.phone,
            address: teacher.address,
            profilePicture: teacher.profilePicture,
            bio: teacher.bio,
            status: teacher.status,
            hireDate: teacher.hireDate,
            terminationDate: teacher.terminationDate,
            terminationReason: teacher.terminationReason,
            isApproved: teacher.isApproved,
            approvedAt: teacher.approvedAt,
            approvedBy: (teacher as any).approvedBy,
            rejectionReason: teacher.rejectionReason,
            subjects: teacher.subjects,
            education: teacher.education,
            experience: teacher.experience,
            totalExperience: (teacher as any).totalExperience,
            documents: teacher.documents,
            emergencyContact: teacher.emergencyContact,
            performanceMetrics: teacher.performanceMetrics,
            preferences: teacher.preferences,
            school: (teacher as any).schoolId,
            user: (teacher as any).userId,
            classes: classes.map(c => ({
              id: c.id,
              className: c.className,
              classCode: c.classCode,
              subject: c.subject,
              status: c.status,
              currentStudents: c.currentStudents,
              maxStudents: c.maxStudents,
              semester: (c as any).semester,
            })),
            createdAt: teacher.createdAt,
            updatedAt: teacher.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update teacher details (School Admin)
   * @route PUT /api/v1/school/teachers/:id
   * @access Private (School Admin)
   */
  async updateTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;
      const updateData = req.body;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Update allowed fields
      const allowedFields = [
        "fullName",
        "dateOfBirth",
        "gender",
        "phone",
        "address",
        "bio",
        "status",
        "hireDate",
        "subjects",
        "education",
        "experience",
        "emergencyContact",
        "preferences",
      ];

      for (const field of allowedFields) {
        if (updateData[field] !== undefined) {
          (teacher as any)[field] = updateData[field];
        }
      }

      await teacher.save();

      logger.info(`Teacher updated: ${teacher.id}`, {
        teacherId: teacher.id,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher updated successfully",
        data: {
          teacher: {
            id: teacher.id,
            employeeId: teacher.employeeId,
            fullName: teacher.fullName,
            status: teacher.status,
            updatedAt: teacher.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error updating teacher:", error);

      if (error.name === "ValidationError") {
        const errors = Object.values(error.errors).map(
          (err: any) => err.message,
        );
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: errors.join(", "),
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to update teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete teacher
   * @route DELETE /api/v1/school/teachers/:id
   * @access Private (School Admin)
   */
  async deleteTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Check if teacher has assigned classes
      const assignedClasses = await Class.countDocuments({
        teacherId: teacher._id,
        status: { $in: ["active", "scheduled"] },
      });

      if (assignedClasses > 0) {
        res.status(400).json({
          success: false,
          message: `Cannot delete teacher. They have ${assignedClasses} active classes assigned. Please reassign or complete the classes first.`,
          error: "Teacher has active classes",
        } as APIResponse);
        return;
      }

      await Teacher.findByIdAndDelete(id);

      logger.info(`Teacher deleted: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher deleted successfully",
        data: {
          deletedTeacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Approve teacher
   * @route PUT /api/v1/school/teachers/:id/approve
   * @access Private (School Admin)
   */
  async approveTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      if (teacher.isApproved) {
        res.status(400).json({
          success: false,
          message: "Teacher is already approved",
          error: "Already approved",
        } as APIResponse);
        return;
      }

      await (teacher as any).approve(new mongoose.Types.ObjectId(userId));

      logger.info(`Teacher approved: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        approvedBy: userId,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher approved successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            isApproved: teacher.isApproved,
            approvedAt: teacher.approvedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error approving teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reject teacher
   * @route PUT /api/v1/school/teachers/:id/reject
   * @access Private (School Admin)
   */
  async rejectTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { reason } = req.body;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!reason) {
        res.status(400).json({
          success: false,
          message: "Rejection reason is required",
          error: "Missing reason",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).reject(
        reason,
        new mongoose.Types.ObjectId(userId),
      );

      logger.info(`Teacher rejected: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        reason,
        rejectedBy: userId,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher rejected successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            isApproved: teacher.isApproved,
            rejectionReason: teacher.rejectionReason,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error rejecting teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Terminate teacher
   * @route PUT /api/v1/school/teachers/:id/terminate
   * @access Private (School Admin)
   */
  async terminateTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { reason } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!reason) {
        res.status(400).json({
          success: false,
          message: "Termination reason is required",
          error: "Missing reason",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).terminate(reason);

      // Unassign from all active classes
      await Class.updateMany(
        {
          teacherId: teacher._id,
          status: { $in: ["active", "scheduled"] },
        },
        { $unset: { teacherId: "" } },
      );

      logger.info(`Teacher terminated: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        reason,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher terminated successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            status: teacher.status,
            terminationDate: teacher.terminationDate,
            terminationReason: teacher.terminationReason,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error terminating teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to terminate teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Put teacher on leave
   * @route PUT /api/v1/school/teachers/:id/leave
   * @access Private (School Admin)
   */
  async putOnLeave(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).putOnLeave();

      logger.info(`Teacher put on leave: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher put on leave successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            status: teacher.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error putting teacher on leave:", error);
      res.status(500).json({
        success: false,
        message: "Failed to put teacher on leave",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Reactivate teacher
   * @route PUT /api/v1/school/teachers/:id/reactivate
   * @access Private (School Admin)
   */
  async reactivateTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).reactivate();

      logger.info(`Teacher reactivated: ${teacher.id}`, {
        teacherId: teacher.id,
        fullName: teacher.fullName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher reactivated successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            status: teacher.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error reactivating teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reactivate teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teachers by subject
   * @route GET /api/v1/school/teachers/by-subject/:subject
   * @access Private (School Admin)
   */
  async getTeachersBySubject(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { subject } = req.params;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const teachers = await Teacher.findBySubject(
        subject,
        new mongoose.Types.ObjectId(schoolId),
      )
        .select("fullName employeeId profilePicture subjects status isApproved")
        .sort({ fullName: 1 });

      res.status(200).json({
        success: true,
        message: `${teachers.length} teachers found for subject: ${subject}`,
        data: {
          subject,
          teachers: teachers.map(teacher => ({
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            profilePicture: teacher.profilePicture,
            subjects: teacher.subjects?.filter(s =>
              s.name.toLowerCase().includes(subject.toLowerCase()),
            ),
            status: teacher.status,
            isApproved: teacher.isApproved,
          })),
          totalCount: teachers.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teachers by subject:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher statistics
   * @route GET /api/v1/school/teachers/statistics
   * @access Private (School Admin)
   */
  async getTeacherStatistics(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const statistics = await Teacher.getStatistics(
        new mongoose.Types.ObjectId(schoolId),
      );

      // Get additional statistics
      const additionalStats = await Teacher.aggregate([
        { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
        {
          $group: {
            _id: null,
            totalTeachers: { $sum: 1 },
            approvedTeachers: {
              $sum: { $cond: ["$isApproved", 1, 0] },
            },
            pendingTeachers: {
              $sum: { $cond: [{ $not: "$isApproved" }, 1, 0] },
            },
            activeTeachers: {
              $sum: { $cond: [{ $eq: ["$status", "active"] }, 1, 0] },
            },
            onLeaveTeachers: {
              $sum: { $cond: [{ $eq: ["$status", "on_leave"] }, 1, 0] },
            },
            terminatedTeachers: {
              $sum: { $cond: [{ $eq: ["$status", "terminated"] }, 1, 0] },
            },
            averageExperience: { $avg: "$totalExperience" },
          },
        },
      ]);

      // Get subject distribution
      const subjectDistribution = await Teacher.aggregate([
        { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
        { $unwind: "$subjects" },
        {
          $group: {
            _id: "$subjects.name",
            count: { $sum: 1 },
            teachers: { $addToSet: "$fullName" },
          },
        },
        { $sort: { count: -1 } },
        { $limit: 10 },
      ]);

      const additional = additionalStats[0] || {
        totalTeachers: 0,
        approvedTeachers: 0,
        pendingTeachers: 0,
        activeTeachers: 0,
        onLeaveTeachers: 0,
        terminatedTeachers: 0,
        averageExperience: 0,
      };

      res.status(200).json({
        success: true,
        message: "Teacher statistics retrieved successfully",
        data: {
          statistics: {
            ...additional,
            averageExperience: Math.round(additional.averageExperience || 0),
            subjectDistribution: subjectDistribution.map(item => ({
              subject: item._id,
              teacherCount: item.count,
              teachers: item.teachers.slice(0, 5), // Show first 5 teachers
            })),
            statusBreakdown: statistics,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teachers by approval status with advanced filtering and pagination
   * @route GET /api/v1/school/teachers/pending
   * @access Private (School Admin)
   * @query approvalStatus, status, subject, search, page, limit, sortBy, sortOrder, dateFrom, dateTo
   */
  async getTeachersByStatus(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const {
        approvalStatus = "pending", // Default to pending for backward compatibility
        status,
        subject,
        search,
        page = 1,
        limit = 10,
        sortBy = "createdAt",
        sortOrder = "desc",
        dateFrom,
        dateTo,
        hasDocuments,
        isEmailVerified,
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Validate approvalStatus
      const validApprovalStatuses = Object.values(PROFILE_APPROVAL_STATUS);
      if (!validApprovalStatuses.includes(approvalStatus as any)) {
        res.status(400).json({
          success: false,
          message: `Invalid approval status. Must be one of: ${validApprovalStatuses.join(", ")}`,
          error: "Invalid approval status",
        } as APIResponse);
        return;
      }

      // Build query
      const schoolObjectId = new mongoose.Types.ObjectId(schoolId);
      console.log("getTeachersByStatus - schoolId from token:", schoolId);
      console.log("getTeachersByStatus - schoolObjectId:", schoolObjectId);
      console.log("getTeachersByStatus - approvalStatus:", approvalStatus);

      const query: any = {
        schoolId: schoolObjectId,
        approvalStatus: approvalStatus as string,
      };
      console.log("getTeachersByStatus - query:", query);

      // Add optional filters
      if (status) query.status = status;
      if (subject) query["subjects.name"] = { $regex: subject, $options: "i" };
      if (hasDocuments !== undefined) {
        if (hasDocuments === "true") {
          query.documents = { $exists: true, $not: { $size: 0 } };
        } else {
          query.$or = [
            { documents: { $exists: false } },
            { documents: { $size: 0 } },
          ];
        }
      }

      // Date range filter
      if (dateFrom || dateTo) {
        query.createdAt = {};
        if (dateFrom) {
          query.createdAt.$gte = new Date(dateFrom as string);
        }
        if (dateTo) {
          query.createdAt.$lte = new Date(dateTo as string);
        }
      }

      // Search filter
      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { employeeId: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
          { phone: { $regex: search, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = [
        "createdAt",
        "fullName",
        "employeeId",
        "approvalStatus",
        "status",
      ];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "createdAt";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      ); // Max 100 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [teachers, totalCount, statusCounts] = await Promise.all([
        Teacher.find({ ...query })
          .populate("userId", "email isEmailVerified")
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .lean(), // Use lean() for better performance
        Teacher.countDocuments(query),
        // Get counts for all approval statuses
        Teacher.aggregate([
          { $match: { schoolId: schoolObjectId } },
          {
            $group: {
              _id: "$approvalStatus",
              count: { $sum: 1 },
            },
          },
        ]),
      ]);

      console.log("getTeachersByStatus - teachers found:", teachers.length);
      console.log("getTeachersByStatus - totalCount:", totalCount);
      console.log("getTeachersByStatus - statusCounts:", statusCounts);

      // Format status counts
      const statusCountMap: Record<string, number> = {};
      for (const item of statusCounts) {
        statusCountMap[item._id] = item.count;
      }

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      // Format response data
      const formattedTeachers = teachers.map(teacher => ({
        id: teacher._id,
        employeeId: teacher.employeeId,
        fullName: teacher.fullName,
        email: (teacher.userId as any)?.email,
        phone: teacher.phone,
        profilePicture: teacher.profilePicture,
        subjects: teacher.subjects,
        education: teacher.education,
        experience: teacher.experience,
        documents: teacher.documents,
        status: teacher.status,
        approvalStatus: teacher.approvalStatus,
        isApproved: teacher.isApproved,
        approvedAt: teacher.approvedAt,
        approvedBy: teacher.approvedBy,
        rejectionReason: teacher.rejectionReason,
        createdAt: teacher.createdAt,
        updatedAt: teacher.updatedAt,
        isEmailVerified: (teacher.userId as any)?.isEmailVerified,
        hasDocuments: teacher.documents && teacher.documents.length > 0,
      }));

      res.status(200).json({
        success: true,
        message: `${totalCount} teachers found with ${typeof approvalStatus === "string" ? approvalStatus : "all"} status`,
        data: {
          teachers: formattedTeachers,
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            approvalStatus,
            status: status || null,
            subject: subject || null,
            search: search || null,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            hasDocuments: hasDocuments || null,
            isEmailVerified: isEmailVerified || null,
          },
          statistics: {
            currentStatus: {
              status: approvalStatus,
              count: totalCount,
            },
            allStatuses: {
              pending: statusCountMap.pending || 0,
              approved: statusCountMap.approved || 0,
              rejected: statusCountMap.rejected || 0,
              cancelled: statusCountMap.cancelled || 0,
            },
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teachers by status:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get pending teachers (backward compatibility)
   * @route GET /api/v1/school/teachers/pending
   * @access Private (School Admin)
   * @deprecated Use getTeachersByStatus with approvalStatus=pending instead
   */
  async getPendingTeachers(req: AuthRequest, res: Response): Promise<void> {
    // Redirect to the new method with pending status
    req.query.approvalStatus = "pending";
    return this.getTeachersByStatus(req, res);
  }

  /**
   * Bulk approve teachers
   * @route POST /api/v1/school/teachers/bulk-approve
   * @access Private (School Admin)
   */
  async bulkApproveTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { teacherIds } = req.body;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;

      if (
        !teacherIds ||
        !Array.isArray(teacherIds) ||
        teacherIds.length === 0
      ) {
        res.status(400).json({
          success: false,
          message: "Teacher IDs array is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Validate all teacher IDs
      const validTeacherIds = teacherIds.filter(id =>
        mongoose.Types.ObjectId.isValid(id),
      );

      if (validTeacherIds.length !== teacherIds.length) {
        res.status(400).json({
          success: false,
          message: "Some teacher IDs are invalid",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Find teachers that belong to this school and are pending
      const teachers = await Teacher.find({
        _id: { $in: validTeacherIds },
        schoolId: new mongoose.Types.ObjectId(schoolId),
        approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
      });

      if (teachers.length === 0) {
        res.status(404).json({
          success: false,
          message: "No pending teachers found for the provided IDs",
          error: "No teachers found",
        } as APIResponse);
        return;
      }

      // Approve all teachers
      const approvedTeachers = [];
      const errors = [];

      for (const teacher of teachers) {
        try {
          await (teacher as any).approve(new mongoose.Types.ObjectId(userId));
          approvedTeachers.push({
            id: teacher._id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          });
        } catch (error) {
          errors.push({
            teacherId: teacher._id,
            fullName: teacher.fullName,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      logger.info(`Bulk teacher approval completed`, {
        schoolId,
        approvedCount: approvedTeachers.length,
        errorCount: errors.length,
        approvedBy: userId,
      });

      res.status(200).json({
        success: true,
        message: `Successfully approved ${approvedTeachers.length} teachers`,
        data: {
          approved: approvedTeachers,
          errors: errors.length > 0 ? errors : undefined,
          summary: {
            totalRequested: teacherIds.length,
            totalApproved: approvedTeachers.length,
            totalErrors: errors.length,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error in bulk teacher approval:", error);
      res.status(500).json({
        success: false,
        message: "Failed to approve teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Bulk reject teachers
   * @route POST /api/v1/school/teachers/bulk-reject
   * @access Private (School Admin)
   */
  async bulkRejectTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { teacherIds, reason } = req.body;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;

      if (
        !teacherIds ||
        !Array.isArray(teacherIds) ||
        teacherIds.length === 0
      ) {
        res.status(400).json({
          success: false,
          message: "Teacher IDs array is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!reason || typeof reason !== "string" || reason.trim().length === 0) {
        res.status(400).json({
          success: false,
          message: "Rejection reason is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Validate all teacher IDs
      const validTeacherIds = teacherIds.filter(id =>
        mongoose.Types.ObjectId.isValid(id),
      );

      if (validTeacherIds.length !== teacherIds.length) {
        res.status(400).json({
          success: false,
          message: "Some teacher IDs are invalid",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Find teachers that belong to this school and are pending
      const teachers = await Teacher.find({
        _id: { $in: validTeacherIds },
        schoolId: new mongoose.Types.ObjectId(schoolId),
        approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
      });

      if (teachers.length === 0) {
        res.status(404).json({
          success: false,
          message: "No pending teachers found for the provided IDs",
          error: "No teachers found",
        } as APIResponse);
        return;
      }

      // Reject all teachers
      const rejectedTeachers = [];
      const errors = [];

      for (const teacher of teachers) {
        try {
          await (teacher as any).reject(
            reason.trim(),
            new mongoose.Types.ObjectId(userId),
          );
          rejectedTeachers.push({
            id: teacher._id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          });
        } catch (error) {
          errors.push({
            teacherId: teacher._id,
            fullName: teacher.fullName,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      logger.info(`Bulk teacher rejection completed`, {
        schoolId,
        rejectedCount: rejectedTeachers.length,
        errorCount: errors.length,
        rejectedBy: userId,
        reason: reason.trim(),
      });

      res.status(200).json({
        success: true,
        message: `Successfully rejected ${rejectedTeachers.length} teachers`,
        data: {
          rejected: rejectedTeachers,
          errors: errors.length > 0 ? errors : undefined,
          summary: {
            totalRequested: teacherIds.length,
            totalRejected: rejectedTeachers.length,
            totalErrors: errors.length,
            reason: reason.trim(),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error in bulk teacher rejection:", error);
      res.status(500).json({
        success: false,
        message: "Failed to reject teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Change teacher approval status (approve/reject) in one API
   * @route PUT /api/v1/school/teachers/:id/status
   * @access Private (School Admin)
   */
  async changeTeacherStatus(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { status, reason } = req.body;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;
      console.log("status", status);
      console.log("reason", reason);
      console.log("id", id);
      console.log("schoolId", schoolId);
      console.log("userId", userId);

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!status || !["approve", "reject"].includes(status)) {
        res.status(400).json({
          success: false,
          message: "Status must be either 'approve' or 'reject'",
          error: "Invalid status",
        } as APIResponse);
        return;
      }

      if (status === "reject" && (!reason || reason.trim().length < 10)) {
        res.status(400).json({
          success: false,
          message:
            "Rejection reason is required and must be at least 10 characters",
          error: "Invalid reason",
        } as APIResponse);
        return;
      }

      // Find teacher belonging to this school
      const teacher = await Teacher.findOne({
        _id: id,
        // schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      console.log("teacher", teacher);

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Check if teacher is already in the requested status
      if (status === "approve" && teacher.approvalStatus === "approved") {
        res.status(400).json({
          success: false,
          message: "Teacher is already approved",
          error: "Already approved",
        } as APIResponse);
        return;
      }

      if (status === "reject" && teacher.approvalStatus === "rejected") {
        res.status(400).json({
          success: false,
          message: "Teacher is already rejected",
          error: "Already rejected",
        } as APIResponse);
        return;
      }

      // Change status based on action
      if (status === "approve") {
        await (teacher as any).approve(new mongoose.Types.ObjectId(userId));

        logger.info(`Teacher approved: ${teacher.id}`, {
          teacherId: teacher.id,
          fullName: teacher.fullName,
          approvedBy: userId,
          schoolId,
        });

        res.status(200).json({
          success: true,
          message: "Teacher approved successfully",
          data: {
            teacher: {
              id: teacher.id,
              fullName: teacher.fullName,
              employeeId: teacher.employeeId,
              approvalStatus: teacher.approvalStatus,
              status: teacher.status,
              approvedAt: teacher.approvedAt,
              approvedBy: teacher.approvedBy,
            },
          },
        } as APIResponse);
      } else if (status === "reject") {
        await (teacher as any).reject(
          reason.trim(),
          new mongoose.Types.ObjectId(userId),
        );

        logger.info(`Teacher rejected: ${teacher.id}`, {
          teacherId: teacher.id,
          fullName: teacher.fullName,
          reason: reason.trim(),
          rejectedBy: userId,
          schoolId,
        });

        res.status(200).json({
          success: true,
          message: "Teacher rejected successfully",
          data: {
            teacher: {
              id: teacher.id,
              fullName: teacher.fullName,
              employeeId: teacher.employeeId,
              approvalStatus: teacher.approvalStatus,
              status: teacher.status,
              rejectionReason: teacher.rejectionReason,
              approvedBy: teacher.approvedBy,
            },
          },
        } as APIResponse);
      }
    } catch (error) {
      logger.error("Error changing teacher status:", error);
      res.status(500).json({
        success: false,
        message: "Failed to change teacher status",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Verify teacher document
   * @route PUT /api/v1/school/teachers/:id/documents/:fileName/verify
   * @access Private (School Admin)
   */
  async verifyDocument(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, fileName } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).verifyDocument(fileName);

      logger.info(`Document verified for teacher: ${teacher.id}`, {
        teacherId: teacher.id,
        fileName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Document verified successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
          },
          verifiedDocument: fileName,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error verifying document:", error);
      res.status(500).json({
        success: false,
        message: "Failed to verify document",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Add subject to teacher
   * @route POST /api/v1/school/teachers/:id/subjects
   * @access Private (School Admin)
   */
  async addSubject(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const subjectInfo = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!subjectInfo.name) {
        res.status(400).json({
          success: false,
          message: "Subject name is required",
          error: "Missing subject name",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).addSubject(subjectInfo);

      logger.info(`Subject added to teacher: ${teacher.id}`, {
        teacherId: teacher.id,
        subject: subjectInfo.name,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Subject added successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            subjects: teacher.subjects,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error adding subject:", error);
      res.status(500).json({
        success: false,
        message: "Failed to add subject",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Remove subject from teacher
   * @route DELETE /api/v1/school/teachers/:id/subjects/:subjectName
   * @access Private (School Admin)
   */
  async removeSubject(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, subjectName } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      await (teacher as any).removeSubject(subjectName);

      logger.info(`Subject removed from teacher: ${teacher.id}`, {
        teacherId: teacher.id,
        subject: subjectName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Subject removed successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            subjects: teacher.subjects,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error removing subject:", error);
      res.status(500).json({
        success: false,
        message: "Failed to remove subject",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update teacher performance metrics
   * @route PUT /api/v1/school/teachers/:id/performance
   * @access Private (School Admin)
   */
  async updatePerformanceMetrics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const metrics = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Update performance metrics
      teacher.performanceMetrics = {
        ...teacher.performanceMetrics,
        ...metrics,
        lastEvaluationDate: new Date(),
      };

      await teacher.save();

      logger.info(`Performance metrics updated for teacher: ${teacher.id}`, {
        teacherId: teacher.id,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Performance metrics updated successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            performanceMetrics: teacher.performanceMetrics,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error updating performance metrics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update performance metrics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all classes for a specific teacher
   * @route GET /api/v1/teacher/classes
   * @access Private (Teacher)
   */
  // async getTeacherClasses(req: AuthRequest, res: Response): Promise<void> {
  //   try {
  //     const teacherId = req.user?.userId;
  //     const getTeacherById = await Teacher.findOne({ userId: teacherId });
  //     console.log("getTeacherClasses - teacherId from token:", teacherId);
  //     console.log("getTeacherClasses - teacherId from token:", req.user);
  //     if (!teacherId) {
  //       res.status(400).json({
  //         success: false,
  //         message: "Teacher ID is required",
  //         error: "Invalid request",
  //       } as APIResponse);
  //       return;
  //     }

  //     // Extract pagination and filter parameters
  //     const {
  //       page = 1,
  //       limit = 10,
  //       sortBy = "createdAt",
  //       sortOrder = "desc",
  //       search,
  //       status,
  //       isActive,
  //       semesterId,
  //       subject,
  //     } = req.query;

  //     // Build query
  //     const query: any = { teacherId: new mongoose.Types.ObjectId(teacherId) };
  //     // Add optional filters
  //     console.log("query:", query);

  //     if (search) {
  //       query.$or = [
  //         { className: { $regex: search, $options: "i" } },
  //         { subject: { $regex: search, $options: "i" } },
  //         { description: { $regex: search, $options: "i" } },
  //       ];
  //     }

  //     if (status) {
  //       query.status = status;
  //     }

  //     if (isActive !== undefined) {
  //       query.isActive = isActive === "true";
  //     }

  //     if (semesterId) {
  //       if (!mongoose.Types.ObjectId.isValid(semesterId as string)) {
  //         res.status(400).json({
  //           success: false,
  //           message: "Invalid semester ID format",
  //           error: "Invalid semester ID",
  //         } as APIResponse);
  //         return;
  //       }
  //       query.semesterId = new mongoose.Types.ObjectId(semesterId as string);
  //     }

  //     if (subject) {
  //       query.subject = { $regex: subject, $options: "i" };
  //     }

  //     // Build sort object
  //     const sort: any = {};
  //     const validSortFields = [
  //       "className",
  //       "subject",
  //       "createdAt",
  //       "status",
  //       "isActive",
  //       "startDate",
  //       "endDate",
  //     ];
  //     const sortField = validSortFields.includes(sortBy as string)
  //       ? (sortBy as string)
  //       : "createdAt";
  //     sort[sortField] = sortOrder === "desc" ? -1 : 1;

  //     // Calculate pagination
  //     const pageNum = Math.max(1, Number.parseInt(page as string, 10));
  //     const limitNum = Math.min(
  //       100,
  //       Math.max(1, Number.parseInt(limit as string, 10)),
  //     ); // Max 100 per page
  //     const skip = (pageNum - 1) * limitNum;

  //     // Execute queries in parallel
  //     const [classes, totalCount, statusCounts] = await Promise.all([
  //       Class.find({ ...query })
  //         .populate("schoolId", "schoolName")
  //         .populate("semesterId", "semesterName academicYear startDate endDate")
  //         .populate("studentIds", "fullName studentId profilePicture")
  //         .sort(sort)
  //         .skip(skip)
  //         .limit(limitNum)
  //         .lean(),
  //       Class.countDocuments({ ...query }),
  //       Class.aggregate([
  //         { $match: { teacherId: new mongoose.Types.ObjectId(teacherId) } },
  //         {
  //           $group: {
  //             _id: "$status",
  //             count: { $sum: 1 },
  //           },
  //         },
  //       ]),
  //     ]);

  //     // Format status counts
  //     const statusCountMap: Record<string, number> = {};
  //     for (const item of statusCounts) {
  //       statusCountMap[item._id] = item.count;
  //     }

  //     // Calculate pagination info
  //     const totalPages = Math.ceil(totalCount / limitNum);
  //     const hasNextPage = pageNum < totalPages;
  //     const hasPrevPage = pageNum > 1;

  //     // Format response data
  //     const formattedClasses = classes.map(classItem => ({
  //       id: classItem._id,
  //       className: classItem.className,
  //       subject: classItem.subject,
  //       description: classItem.description,
  //       status: classItem.status,
  //       isActive: classItem.isActive,
  //       maxStudents: classItem.maxStudents,
  //       currentStudents: classItem.studentIds?.length || 0,
  //       schedule: classItem.schedule,
  //       school: {
  //         id: (classItem.schoolId as any)?._id,
  //         name: (classItem.schoolId as any)?.schoolName,
  //       },
  //       semester: {
  //         id: (classItem.semesterId as any)?._id,
  //         name: (classItem.semesterId as any)?.semesterName,
  //         academicYear: (classItem.semesterId as any)?.academicYear,
  //         startDate: (classItem.semesterId as any)?.startDate,
  //         endDate: (classItem.semesterId as any)?.endDate,
  //       },
  //       students:
  //         (classItem.studentIds as any[])?.map(student => ({
  //           id: student._id,
  //           fullName: student.fullName,
  //           studentId: student.studentId,
  //           profilePicture: student.profilePicture,
  //         })) || [],
  //       createdAt: classItem.createdAt,
  //       updatedAt: classItem.updatedAt,
  //     }));

  //     res.status(200).json({
  //       success: true,
  //       message: `${totalCount} classes found`,
  //       data: {
  //         classes: formattedClasses,
  //         pagination: {
  //           currentPage: pageNum,
  //           totalPages,
  //           totalCount,
  //           limit: limitNum,
  //           hasNextPage,
  //           hasPrevPage,
  //           nextPage: hasNextPage ? pageNum + 1 : null,
  //           prevPage: hasPrevPage ? pageNum - 1 : null,
  //         },
  //         filters: {
  //           search: search || null,
  //           status: status || null,
  //           isActive: isActive || null,
  //           semesterId: semesterId || null,
  //           subject: subject || null,
  //         },
  //         sort: {
  //           field: sortField,
  //           order: sortOrder,
  //         },
  //         statistics: {
  //           totalClasses: totalCount,
  //           statusBreakdown: statusCountMap,
  //         },
  //       },
  //     } as APIResponse);
  //   } catch (error) {
  //     logger.error("Error fetching teacher classes:", error);
  //     res.status(500).json({
  //       success: false,
  //       message: "Failed to fetch classes",
  //       error: "Internal server error",
  //     } as APIResponse);
  //   }
  // }

  async getTeacherClasses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
        } as APIResponse);
        return;
      }

      // 1️⃣ Get teacher profile
      const teacher = await Teacher.findOne({ userId });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      // 2️⃣ Query params
      const {
        page = 1,
        limit = 10,
        sortBy = "createdAt",
        sortOrder = "desc",
        search,
        status,
        isActive,
        semesterId,
        subject,
        modality,
        department,
        courseType,
      } = req.query;

      // 3️⃣ Build query (🔥 FIXED)
      const query: any = {
        teacherIds: { $in: [teacher._id] },
      };

      if (search) {
        query.$or = [
          { className: { $regex: search, $options: "i" } },
          { subject: { $regex: search, $options: "i" } },
          { description: { $regex: search, $options: "i" } },
        ];
      }

      if (status) query.status = status;
      if (isActive !== undefined) query.isActive = isActive === "true";

      if (semesterId) {
        if (!mongoose.Types.ObjectId.isValid(semesterId as string)) {
          res.status(400).json({
            success: false,
            message: "Invalid semester ID",
          } as APIResponse);
          return;
        }
        query.semesterId = mongoose.Types.ObjectId.createFromHexString(
          semesterId as string,
        );
      }

      if (subject) {
        query.subject = { $regex: subject, $options: "i" };
      }

      if (modality) query.modality = modality;
      if (department) query.department = { $regex: department, $options: "i" };
      if (courseType) query.courseType = { $regex: courseType, $options: "i" };

      // 4️⃣ Sorting
      const validSortFields = [
        "className",
        "subject",
        "createdAt",
        "status",
        "isActive",
        "startDate",
        "endDate",
      ];

      const sortField = validSortFields.includes(sortBy as string)
        ? sortBy
        : "createdAt";

      const sort = {
        [sortField as string]: sortOrder === "desc" ? -1 : 1,
      };

      // 5️⃣ Pagination
      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      // 6️⃣ Fetch data
      const [classes, totalCount, statusCounts] = await Promise.all([
        Class.find({ ...query })
          .populate("schoolId", "schoolName")
          .populate("semesterId", "semesterName academicYear startDate endDate")
          .sort(sort as any)
          .skip(skip)
          .limit(limitNum)
          .lean(),

        Class.countDocuments({ ...query }),

        Class.aggregate([
          { $match: { teacherIds: { $in: [teacher._id] } } },
          { $group: { _id: "$status", count: { $sum: 1 } } },
        ]),
      ]);

      // 7️⃣ Status breakdown
      const statusBreakdown: Record<string, number> = {};
      for (const item of statusCounts) {
        statusBreakdown[item._id] = item.count;
      }

      // 8️⃣ Format response
      const formattedClasses = classes.map(
        (classItem: Record<string, unknown>) => formatClassForMobile(classItem),
      );

      // 9️⃣ Response
      res.status(200).json({
        success: true,
        message: `${totalCount} classes found`,
        data: {
          classes: formattedClasses,
          pagination: {
            currentPage: pageNum,
            totalPages: Math.ceil(totalCount / limitNum),
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < Math.ceil(totalCount / limitNum),
            hasPrevPage: pageNum > 1,
          },
          filters: {
            search: search || null,
            status: status || null,
            isActive: isActive || null,
            semesterId: semesterId || null,
            subject: subject || null,
            modality: modality || null,
            department: department || null,
            courseType: courseType || null,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
          statistics: {
            totalClasses: totalCount,
            statusBreakdown,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher classes:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch classes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get class by ID with complete details for teacher
   * @route GET /api/v1/teacher/classes/:id
   * @access Private (Teacher)
   */
  async getTeacherClassById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher profile
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Get class and verify teacher is assigned to it
      const classItem = await Class.findById(id)
        .populate("schoolId", "schoolName address")
        .populate("semesterId", "semesterName academicYear startDate endDate")
        .populate("teacherIds", "fullName employeeId profilePicture")
        .populate("studentIds", "fullName studentId profilePicture email")
        .lean();

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to this class
      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: any) =>
          (t._id as mongoose.Types.ObjectId).toString() ===
          teacherObjectId.toString(),
      );

      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      // Format schedule for response (preserve all entries)
      const scheduleResponse =
        classItem.schedule && Array.isArray(classItem.schedule)
          ? classItem.schedule.map(scheduleItem => ({
              day: scheduleItem.day,
              startTime:
                this.normalizeTimeToHHmm(scheduleItem.startTime) ??
                scheduleItem.startTime,
              endTime:
                this.normalizeTimeToHHmm(scheduleItem.endTime) ??
                scheduleItem.endTime,
              isActive: scheduleItem.isActive ?? false,
            }))
          : [];

      // Final attendance cutoff (if configured in class settings)
      const finalAttendanceTime =
        (classItem as any)?.settings?.finalAttendanceTime || null;

      // Format response
      res.status(200).json({
        success: true,
        message: "Class retrieved successfully",
        data: {
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            credits: classItem.credits,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.studentIds?.length || 0,
            availableSpots:
              classItem.maxStudents &&
              classItem.maxStudents > (classItem.studentIds?.length || 0)
                ? classItem.maxStudents - (classItem.studentIds?.length || 0)
                : 0,
            classRoom: classItem.classRoom,
            classRoomNumber: classItem.classRoomNumber,
            restRoomMinutes: classItem.restRoomMinutes,
            location: {
              lat:
                (classItem as any).location?.latitude == null
                  ? "0.0"
                  : String((classItem as any).location.latitude),
              lng:
                (classItem as any).location?.longitude == null
                  ? "0.0"
                  : String((classItem as any).location.longitude),
            },
            schedule: scheduleResponse,
            finalAttendance: finalAttendanceTime,
            school: classItem.schoolId
              ? {
                  id: (classItem.schoolId as any)._id,
                  name: (classItem.schoolId as any).schoolName,
                  address: (classItem.schoolId as any).address,
                }
              : null,
            semester: classItem.semesterId
              ? {
                  id: (classItem.semesterId as any)._id,
                  name: (classItem.semesterId as any).semesterName,
                  academicYear: (classItem.semesterId as any).academicYear,
                  startDate: (classItem.semesterId as any).startDate,
                  endDate: (classItem.semesterId as any).endDate,
                }
              : null,
            teachers:
              (classItem.teacherIds as any[])?.map((t: any) => ({
                id: t._id,
                fullName: t.fullName,
                employeeId: t.employeeId,
                profilePicture: t.profilePicture,
              })) || [],
            enrolledStudentsCount: classItem.studentIds?.length || 0,
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher class by ID:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all enrolled students for a class (Teacher view)
   * @route GET /api/v1/teacher/classes/:id/students
   * @access Private (Teacher)
   */
  async getTeacherClassEnrolledStudents(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;
      const { page = 1, limit = 20, search } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher profile
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Get class and verify teacher is assigned
      const classItem = await Class.findById(id).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned
      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: mongoose.Types.ObjectId) =>
          t.toString() === teacherObjectId.toString(),
      );

      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      // Build student query
      const studentQuery: any = {
        _id: { $in: classItem.studentIds || [] },
      };

      if (search) {
        studentQuery.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { studentId: { $regex: search, $options: "i" } },
          { email: { $regex: search, $options: "i" } },
        ];
      }

      // Pagination
      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      // Fetch students
      const [students, totalCount] = await Promise.all([
        Student.find({ ...studentQuery })
          .populate("userId", "email isEmailVerified")
          .select(
            "fullName studentId email phone academicLevel profilePicture status approvalStatus createdAt",
          )
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments({ ...studentQuery }),
      ]);

      // Format response
      const formattedStudents = students.map((student: any) => ({
        id: student._id,
        fullName: student.fullName,
        studentId: student.studentId,
        email: student.userId?.email || student.email,
        phone: student.phone,
        academicLevel: student.academicLevel,
        profilePicture: student.profilePicture,
        status: student.status,
        approvalStatus: student.approvalStatus,
        createdAt: student.createdAt,
      }));

      res.status(200).json({
        success: true,
        message: `${totalCount} students found`,
        data: {
          students: formattedStudents,
          pagination: {
            currentPage: pageNum,
            totalPages: Math.ceil(totalCount / limitNum),
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < Math.ceil(totalCount / limitNum),
            hasPrevPage: pageNum > 1,
          },
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching enrolled students:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch enrolled students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student details inside a specific class (Teacher view)
   * @route GET /api/v1/teacher/classes/:classId/students/:id
   * @access Private (Teacher)
   */
  async getStudentInClassById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id: studentId } = req.params;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (
        !mongoose.Types.ObjectId.isValid(classId) ||
        !mongoose.Types.ObjectId.isValid(studentId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID or student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findById(classId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: mongoose.Types.ObjectId) =>
          t.toString() === teacherObjectId.toString(),
      );
      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const studentObjectId = new mongoose.Types.ObjectId(studentId);
      const isEnrolled = (classItem.studentIds as any[])?.some(
        (s: mongoose.Types.ObjectId) => s.toString() === studentId,
      );
      if (!isEnrolled) {
        res.status(404).json({
          success: false,
          message: "Student is not enrolled in this class",
          error: "Student not enrolled",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: studentObjectId,
        schoolId: classItem.schoolId,
      })
        .populate("userId", "email isEmailVerified")
        .populate("schoolId", "schoolName")
        .populate("majorCourseIds", "courseCode courseName")
        .lean();

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const majorCourseIds = (student.majorCourseIds as any[])?.length
        ? (student.majorCourseIds as any[]).map((c: any) => ({
            id: c._id,
            courseCode: c.courseCode,
            courseName: c.courseName,
          }))
        : [];

      res.status(200).json({
        success: true,
        message: "Student retrieved successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            dateOfBirth: student.dateOfBirth,
            gender: student.gender,
            phone: student.phone,
            email: (student.userId as any)?.email,
            isEmailVerified: (student.userId as any)?.isEmailVerified,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            status: student.status,
            isApproved: student.isApproved,
            profilePicture: student.profilePicture,
            school: {
              id: (student.schoolId as any)?._id,
              name: (student.schoolId as any)?.schoolName,
            },
            majorCourseIds,
            guardians: student.guardians,
            academicRecord: student.academicRecord,
            preferences: student.preferences,
            medicalInfo: student.medicalInfo,
            documents: student.documents,
            createdAt: student.createdAt,
            updatedAt: student.updatedAt,
          },
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student in class error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve student",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update class schedule/timings (Teacher-assigned classes only)
   * @route PUT /api/v1/teacher/classes/:id/schedule
   * @access Private (Teacher)
   */
  async updateClassSchedule(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;
      const { schedule, restRoomMinutes, finalAttendanceTime } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Validate schedule payload; accept only HH:mm (24h), no timezone conversion
      const allowedDays = new Set([
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ]);
      const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/; // HH:mm 24h

      if (!Array.isArray(schedule) || schedule.length === 0) {
        res.status(400).json({
          success: false,
          message:
            "Schedule is required and must be a non-empty array of { day, startTime, endTime, isActive }",
        } as APIResponse);
        return;
      }

      const normalizedSchedule: {
        day: string;
        startTime: string;
        endTime: string;
        isActive: boolean;
      }[] = [];

      for (const item of schedule) {
        const day = typeof item?.day === "string" ? item.day.toLowerCase() : "";
        if (!allowedDays.has(day)) {
          res.status(400).json({
            success: false,
            message: `Invalid day '${item?.day}'. Must be one of monday-sunday`,
          } as APIResponse);
          return;
        }
        const startTime =
          typeof item?.startTime === "string" ? item.startTime.trim() : "";
        const endTime =
          typeof item?.endTime === "string" ? item.endTime.trim() : "";
        if (startTime == null || endTime == null) {
          res.status(400).json({
            success: false,
            message: "startTime and endTime must be HH:mm (24h) format",
          } as APIResponse);
          return;
        }
        if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
          res.status(400).json({
            success: false,
            message: "startTime and endTime must be HH:mm (24h) format",
          } as APIResponse);
          return;
        }
        if (item.isActive && startTime >= endTime) {
          res.status(400).json({
            success: false,
            message: "startTime must be earlier than endTime for active slots",
          } as APIResponse);
          return;
        }
        normalizedSchedule.push({
          day,
          startTime,
          endTime,
          isActive: item.isActive ?? false,
        });
      }

      if (
        restRoomMinutes !== undefined &&
        (Number.isNaN(Number(restRoomMinutes)) ||
          Number(restRoomMinutes) < 0 ||
          Number(restRoomMinutes) > 60)
      ) {
        res.status(400).json({
          success: false,
          message: "restRoomMinutes must be between 0 and 60",
        } as APIResponse);
        return;
      }

      let normalizedFinalAttendanceTime: string | undefined;
      if (finalAttendanceTime !== undefined && finalAttendanceTime !== null) {
        normalizedFinalAttendanceTime =
          typeof finalAttendanceTime === "string"
            ? finalAttendanceTime.trim()
            : "";
      }
      if (
        finalAttendanceTime !== undefined &&
        finalAttendanceTime !== null &&
        !timeRegex.test(normalizedFinalAttendanceTime ?? "")
      ) {
        res.status(400).json({
          success: false,
          message: "finalAttendanceTime must be HH:mm (24h) format",
        } as APIResponse);
        return;
      }

      // Resolve teacher
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Get class and verify teacher assignment
      const classItem = await Class.findById(id);
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const isTeacherAssigned =
        classItem.teacherIds?.some(tid => tid.equals(teacher._id)) ?? false;

      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      // Apply updates (store UTC time-of-day as HH:mm for compatibility)
      classItem.schedule = normalizedSchedule;

      if (restRoomMinutes !== undefined) {
        classItem.restRoomMinutes = Number(restRoomMinutes);
      }

      if (normalizedFinalAttendanceTime !== undefined) {
        classItem.settings = classItem.settings || {
          allowSelfEnrollment: false,
          requireApproval: true,
          isPublic: false,
        };
        (classItem.settings as any).finalAttendanceTime =
          normalizedFinalAttendanceTime;
      }

      await classItem.save();

      res.status(200).json({
        success: true,
        message: "Class schedule updated",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            schedule: classItem.schedule,
            restRoomMinutes: classItem.restRoomMinutes,
            finalAttendanceTime:
              (classItem.settings as any)?.finalAttendanceTime ?? null,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update class schedule error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update class schedule",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const teacherController = new TeacherController();
